/**
 * Lead hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leadService } from '@/api'
import { useUIStore } from '@/stores/uiStore'
import { handleApiError } from '@/lib/apiErrorHandler'
import type {
  LeadCreate,
  LeadUpdate,
  LeadFilterParams,
  LeadTransition,
  LeadResponse,
  LeadListResponse,
} from '@/types/api'

/**
 * Query keys for leads
 */
export const leadKeys = {
  all: ['leads'] as const,
  lists: () => [...leadKeys.all, 'list'] as const,
  list: (filters: LeadFilterParams) => [...leadKeys.lists(), filters] as const,
  details: () => [...leadKeys.all, 'detail'] as const,
  detail: (id: string) => [...leadKeys.details(), id] as const,
}

/**
 * Hook to get paginated leads
 */
export function useLeads(params?: LeadFilterParams) {
  return useQuery<LeadListResponse>({
    queryKey: leadKeys.list(params || {}),
    queryFn: () => leadService.getLeads(params),
  })
}

/**
 * Hook to get a single lead
 */
export function useLead(id: string) {
  return useQuery<LeadResponse>({
    queryKey: leadKeys.detail(id),
    queryFn: () => leadService.getLead(id),
    enabled: !!id,
  })
}

/**
 * Hook for creating a lead
 */
export function useCreateLead() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (data: LeadCreate) => leadService.createLead(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      addToast({
        title: 'Lead created',
        description: `${data.first_name} ${data.last_name} has been added`,
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to create lead')
    },
  })
}

/**
 * Hook for updating a lead
 */
export function useUpdateLead(id: string) {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (data: LeadUpdate) => leadService.updateLead(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(leadKeys.detail(id), data)
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      addToast({
        title: 'Lead updated',
        description: 'Changes have been saved',
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to update lead')
    },
  })
}

/**
 * Hook for deleting a lead
 */
export function useDeleteLead() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (id: string) => leadService.deleteLead(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: leadKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      addToast({
        title: 'Lead deleted',
        description: 'The lead has been removed',
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to delete lead')
    },
  })
}

/**
 * Hook for transitioning lead status
 */
export function useTransitionLead(id: string) {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (data: LeadTransition) => leadService.transitionLead(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(leadKeys.detail(id), data)
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      addToast({
        title: 'Status updated',
        description: `Lead moved to ${data.lead_status.replace('_', ' ')}`,
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to update status')
    },
  })
}
